/**
 * Schemas Zod das rotas de `/leads` e `/clientes` (RNF-30, decisão A7).
 *
 * Fonte única: a API valida com estes schemas e os formulários do `apps/web`
 * importam os mesmos. Validação no cliente é conveniência; a garantia é a do
 * servidor (RN-11: "validar **no servidor** também").
 *
 * Três decisões que atravessam o arquivo:
 *
 *  1. **`status` não entra em nenhum corpo de criação.** Quem decide o status
 *     inicial é o servidor (§6.2), e mudança de status só acontece por
 *     `POST /leads/{uuid}/interacoes` (RN-22).
 *  2. **`temperatura` nunca entra.** É derivada (⚠️ S6 / RN-21).
 *  3. `.strict()` nos corpos: campo desconhecido é erro, não silêncio. Um cliente
 *     que mandar `"temperatura": "QUENTE"` recebe `422` em vez de acreditar que
 *     funcionou.
 */
import { z } from 'zod'

import {
  zBusca,
  zDataFiltro,
  zDinheiro,
  zIdCatalogo,
  zInstante,
  zMotivoTransferencia,
  zTextoAtendimento,
  zUuid,
} from '../comum/campos'
import { schemaListaDeEnum, schemaPaginacaoCursor } from '../comum/paginacao'
import {
  ORIGENS_LEAD,
  PERCEPCOES,
  STATUS_LEAD,
  TEMPERATURAS,
  TIPOS_INTERACAO,
  zPercepcao,
  zStatusLead,
  zTipoDocumento,
} from './enums'

// ---------------------------------------------------------------------------
// Cliente (TELA-03 / TELA-06)
// ---------------------------------------------------------------------------

const schemaTelefone = z
  .object({
    ddi: z.string().trim().max(4).default('55'),
    numero: z.string().trim().min(8, 'Número de telefone incompleto.').max(20),
  })
  .strict()

const schemaEndereco = z
  .object({
    cep: z.string().trim().max(9).nullish(),
    logradouro: z.string().trim().max(255).nullish(),
    bairro: z.string().trim().max(120).nullish(),
    complemento: z.string().trim().max(120).nullish(),
    cidade: z.string().trim().max(120).nullish(),
    estado: z
      .string()
      .trim()
      .toUpperCase()
      .length(2, 'Use a sigla de 2 letras do estado.')
      .nullish(),
  })
  .strict()

const schemaProfissional = z
  .object({
    cargo: z.string().trim().max(120).nullish(),
    empresa: z.string().trim().max(160).nullish(),
    site: z.string().trim().max(255).nullish(),
  })
  .strict()

/**
 * Dados cadastrais do cliente. `contatoPrincipal` é obrigatório (RN-12) porque é
 * a chave de deduplicação (RN-10): cliente sem telefone normalizado entra na base
 * como duplicata garantida do próximo atendimento da mesma pessoa.
 */
export const schemaDadosCliente = z
  .object({
    nome: z
      .string({ required_error: 'Informe o nome do cliente.' })
      .trim()
      .min(2, 'Informe o nome do cliente.')
      .max(160),
    email: z.string().trim().toLowerCase().email('Informe um e-mail válido.').max(320).nullish(),
    tipoDocumento: zTipoDocumento.nullish(),
    documento: z.string().trim().max(20).nullish(),
    contatoPrincipal: schemaTelefone,
    contatoAlternativo: schemaTelefone.nullish(),
    /** Vazio herda o contato principal (`02-domains.md §3.1`). */
    whatsapp: schemaTelefone.nullish(),
    endereco: schemaEndereco.nullish(),
    profissional: schemaProfissional.nullish(),
  })
  .strict()

export type DadosCliente = z.infer<typeof schemaDadosCliente>

/** `POST /clientes/verificar-duplicata` — pré-checagem da TELA-03 (RN-10). */
export const schemaVerificarDuplicata = z
  .object({
    telefone: z.string().trim().max(25).nullish(),
    documento: z.string().trim().max(20).nullish(),
    email: z.string().trim().toLowerCase().max(320).nullish(),
  })
  .strict()
  .refine(
    (valor) => Boolean(valor.telefone ?? valor.documento ?? valor.email),
    'Informe telefone, documento ou e-mail para verificar.',
  )

export type EntradaVerificarDuplicata = z.infer<typeof schemaVerificarDuplicata>

// ---------------------------------------------------------------------------
// POST /leads — criação manual (FL-04, §6.2)
// ---------------------------------------------------------------------------

const schemaAgendamentoNaCriacao = z
  .object({
    motivoId: zIdCatalogo,
    iniciaEm: zInstante,
    terminaEm: zInstante.nullish(),
    observacao: z.string().trim().max(1000).nullish(),
  })
  .strict()

export const schemaCriarLead = z
  .object({
    cliente: schemaDadosCliente,
    lead: z
      .object({
        unidadeId: zIdCatalogo,
        equipeId: zIdCatalogo,
        /** `null` = criar sem responsável (fila "SEM RESPONSÁVEL" da TELA-02). */
        responsavelUuid: zUuid.nullish(),
        percepcao: zPercepcao.default('NAO_IDENTIFICADO'),
        pontoConversaoId: zIdCatalogo.nullish(),
        produtoAssociadoId: zIdCatalogo.nullish(),
        tagIds: z.array(zIdCatalogo).max(20).default([]),
      })
      .strict(),
    /** Gravada como primeira interação, tipo `CRIACAO` (FL-04 passo 6). */
    observacao: zTextoAtendimento,
    agendamento: schemaAgendamentoNaCriacao.nullish(),
    /**
     * Confirmação explícita de duplicata (RN-10). Só tem efeito para Gestor+; o
     * papel é verificado no caso de uso, não aqui.
     */
    confirmarDuplicata: z.boolean().default(false),
  })
  .strict()

export type EntradaCriarLead = z.infer<typeof schemaCriarLead>

// ---------------------------------------------------------------------------
// PATCH /leads/{uuid} — dados de classificação (TELA-06)
// ---------------------------------------------------------------------------

/**
 * Só o que a TELA-06 chama de cadastral. Fora daqui, de propósito:
 *
 * - `status` → `POST /leads/{uuid}/interacoes` (RN-22);
 * - `responsavelId` / `equipeId` → `POST /leads/{uuid}/transferir` (RN-26, exige motivo);
 * - `percepcao` → vem da última interação (RN-24);
 * - `temperatura`, `chegadaEm` → derivada e imutável.
 */
export const schemaAtualizarLead = z
  .object({
    pontoConversaoId: zIdCatalogo.nullish(),
    produtoAssociadoId: zIdCatalogo.nullish(),
    tagIds: z.array(zIdCatalogo).max(20).optional(),
    valorNegocio: zDinheiro.nullish(),
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarLead = z.infer<typeof schemaAtualizarLead>

// ---------------------------------------------------------------------------
// POST /leads/{uuid}/interacoes — o endpoint central (§6.3)
// ---------------------------------------------------------------------------

export const schemaRegistrarInteracao = z
  .object({
    /** Ausente = anotação pura, sem transição (§6.3, última linha). */
    statusNovo: zStatusLead.optional(),
    mensagem: zTextoAtendimento,
    /** Obrigatória em toda interação manual (RN-24). */
    percepcao: zPercepcao,
    produtoAssociadoId: zIdCatalogo.nullish(),
    agendamento: schemaAgendamentoNaCriacao.nullish(),
    motivoVendaNaoRealizadaId: zIdCatalogo.nullish(),
    vendaRealizadaEm: zInstante.nullish(),
    valorNegocio: zDinheiro.nullish(),
    propostaUuid: zUuid.nullish(),
    /** Alimenta o badge `ABORDAGEM {n}` da TELA-02 (RN-21). */
    registrarTentativaContato: z.boolean().default(false),
  })
  .strict()

export type EntradaRegistrarInteracao = z.infer<typeof schemaRegistrarInteracao>

/** `POST /leads/{uuid}/reabrir` — Gestor+ com justificativa obrigatória (RN-20). */
export const schemaReabrirLead = z
  .object({ justificativa: zTextoAtendimento })
  .strict()

export type EntradaReabrirLead = z.infer<typeof schemaReabrirLead>

// ---------------------------------------------------------------------------
// Transferência (FL-13, §6.5)
// ---------------------------------------------------------------------------

export const schemaTransferirLead = z
  .object({
    /** `null` devolve ao pool da equipe (`responsavel_id = null`). */
    responsavelUuid: zUuid.nullish(),
    equipeId: zIdCatalogo.optional(),
    motivo: zMotivoTransferencia,
  })
  .strict()
  .refine(
    (valor) => valor.responsavelUuid !== undefined || valor.equipeId !== undefined,
    'Informe o novo responsável, a nova equipe, ou os dois.',
  )

export type EntradaTransferirLead = z.infer<typeof schemaTransferirLead>

/** RN-26B: acima deste volume a operação vai para a fila, com relatório. */
export const TRANSFERENCIA_LOTE_LIMITE_SINCRONO = 200

export const schemaTransferirLote = z
  .object({
    leadUuids: z
      .array(zUuid)
      .min(1, 'Selecione ao menos um lead.')
      .max(2000, 'Seleção acima do máximo aceito em uma requisição.'),
    responsavelUuid: zUuid.nullish(),
    equipeId: zIdCatalogo.optional(),
    motivo: zMotivoTransferencia,
  })
  .strict()
  .refine(
    (valor) => valor.responsavelUuid !== undefined || valor.equipeId !== undefined,
    'Informe o novo responsável, a nova equipe, ou os dois.',
  )

export type EntradaTransferirLote = z.infer<typeof schemaTransferirLote>

// ---------------------------------------------------------------------------
// GET /leads — filtros da TELA-04 (§6.1)
// ---------------------------------------------------------------------------

/** Allowlist de ordenação. Campo fora da lista → `422` (§6.6). */
export const CAMPOS_ORDENACAO_LEAD = [
  'chegadaEm',
  'ultimaInteracaoEm',
  'proximoRetornoEm',
  'status',
  'temperatura',
  'clienteNome',
] as const

export type CampoOrdenacaoLead = (typeof CAMPOS_ORDENACAO_LEAD)[number]

/**
 * `responsavelUuid` aceita o literal `nulo` — é o filtro "SEM RESPONSÁVEL" da
 * TELA-02 (§6.5, "Nulo explícito"). O rascunho chama o parâmetro de
 * `responsavelId`; aqui é `responsavelUuid` porque id sequencial de usuário não
 * aparece em contrato (RNF-23), e é o nome que o próprio rascunho usa nos filtros
 * de calendário (§7).
 */
export const schemaFiltrosLead = schemaPaginacaoCursor.extend({
  busca: zBusca,
  status: schemaListaDeEnum(STATUS_LEAD, 'Status'),
  temperatura: schemaListaDeEnum(TEMPERATURAS, 'Temperatura'),
  percepcao: schemaListaDeEnum(PERCEPCOES, 'Percepção'),
  origemTipo: schemaListaDeEnum(ORIGENS_LEAD, 'Origem'),
  unidadeId: zIdCatalogo.optional(),
  equipeId: zIdCatalogo.optional(),
  responsavelUuid: z.union([zUuid, z.literal('nulo')]).optional(),
  pontoConversaoId: zIdCatalogo.optional(),
  produtoAssociadoId: zIdCatalogo.optional(),
  motivoVendaNaoRealizadaId: zIdCatalogo.optional(),
  tagId: zIdCatalogo.optional(),
  chegadaEmDe: zDataFiltro.optional(),
  chegadaEmAte: zDataFiltro.optional(),
  ultimaInteracaoDe: zDataFiltro.optional(),
  ultimaInteracaoAte: zDataFiltro.optional(),
  atrasados: z.enum(['true']).optional(),
  semPrimeiroAtendimento: z.enum(['true']).optional(),
  /** Toggle "Ocultar os leads finalizados" da TELA-04. */
  ocultarFinalizados: z.enum(['true']).optional(),
  /** §6.3: só permitido junto de filtro de período. */
  incluirTotal: z.enum(['true']).optional(),
})

export type FiltrosLead = z.infer<typeof schemaFiltrosLead>

// ---------------------------------------------------------------------------
// GET /leads/{uuid}/interacoes — timeline (§6.4)
// ---------------------------------------------------------------------------

export const schemaFiltrosTimeline = z.object({
  limite: schemaPaginacaoCursor.shape.limite,
  cursor: schemaPaginacaoCursor.shape.cursor,
  tipo: schemaListaDeEnum(TIPOS_INTERACAO, 'Tipo de interação'),
})

export type FiltrosTimeline = z.infer<typeof schemaFiltrosTimeline>

// ---------------------------------------------------------------------------
// GET /painel — TELA-02 (§8)
// ---------------------------------------------------------------------------

export const schemaFiltrosPainel = z.object({
  de: zDataFiltro.optional(),
  ate: zDataFiltro.optional(),
  busca: zBusca,
  equipeId: zIdCatalogo.optional(),
  unidadeId: zIdCatalogo.optional(),
  responsavelUuid: z.union([zUuid, z.literal('nulo')]).optional(),
  apenasSemResponsavel: z.enum(['true']).optional(),
  apenasAtrasados: z.enum(['true']).optional(),
})

export type FiltrosPainel = z.infer<typeof schemaFiltrosPainel>

export const schemaColunaPainel = schemaFiltrosPainel.extend({
  limite: schemaPaginacaoCursor.shape.limite,
  cursor: schemaPaginacaoCursor.shape.cursor,
})

export type FiltrosColunaPainel = z.infer<typeof schemaColunaPainel>
